import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Хук для определения типа устройства и среды запуска
 * Определяет: мобильное устройство, планшет, десктоп, PWA режим
 */

// Функция для получения информации об устройстве (вынесена наружу для использования в useState)
// Дополнительно принимает lastOrientation (beta/gamma) из deviceorientation
const getDeviceInfoInternal = (lastOrientation = null) => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const platform = navigator.platform || '';
    
    // Определение типа устройства
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isAndroid = /Android/.test(userAgent);
    const isMobile = isIOS || isAndroid || /Mobile|Android|iP(hone|od|ad)|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera M(obi|ini)|Blazer|Dolfin|Dolphin|Skyfire|Zune/.test(userAgent);
    
    // Определение планшета
    const isTablet = /iPad/.test(userAgent) || (isAndroid && !/Mobile/.test(userAgent)) || /Tablet/.test(userAgent);
    
    // Определение десктопа
    const isDesktop = !isMobile && !isTablet;
    
    // Определение браузера
    const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
    const isFirefox = /Firefox/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
    const isEdge = /Edge/.test(userAgent) || /Edg/.test(userAgent);
    
    // Определение PWA режима
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isInStandaloneMode = ('standalone' in window.navigator) && window.navigator.standalone;
    const isPWA = isStandalone || (isIOS && isInStandaloneMode);
    
    // Определение размера экрана - сначала по visualViewport, затем fallback на innerWidth/innerHeight
    const viewportWidth = (window.visualViewport && window.visualViewport.width) || window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = (window.visualViewport && window.visualViewport.height) || window.innerHeight || document.documentElement.clientHeight;
    const screenPhysicalWidth = window.screen
      ? (window.screen.availWidth || window.screen.width || viewportWidth)
      : viewportWidth;
    const screenPhysicalHeight = window.screen
      ? (window.screen.availHeight || window.screen.height || viewportHeight)
      : viewportHeight;
    let screenWidth = viewportWidth;
    let screenHeight = viewportHeight;
    
    // Инициализация переменных ориентации
    let isPortrait = false;
    let isLandscape = false;

    // Приоритет 1: deviceorientation (gamma) - самый надежный для PWA
    if (lastOrientation && typeof lastOrientation.gamma === 'number') {
      const absGamma = Math.abs(lastOrientation.gamma);
      // Если gamma > 45, устройство повернуто в landscape
      if (absGamma > 45) {
        isLandscape = true;
        isPortrait = false;
      } else {
        // Если gamma близок к 0, устройство в portrait
        isPortrait = true;
        isLandscape = false;
      }
    }

    // Приоритет 2: screen.orientation API
    if (window.screen && window.screen.orientation) {
      const { angle, type } = window.screen.orientation;
      if (type && typeof type === 'string') {
        if (type.includes('landscape')) {
          isLandscape = true;
          isPortrait = false;
        } else if (type.includes('portrait')) {
          isPortrait = true;
          isLandscape = false;
        }
      }
      if (typeof angle !== 'undefined') {
        if (angle === 0 || angle === 180) {
          isPortrait = true;
          isLandscape = false;
        } else if (angle === 90 || angle === -90 || angle === 270) {
          isLandscape = true;
          isPortrait = false;
        }
      }
    }

    // Приоритет 3: matchMedia
    if (window.matchMedia) {
      const mqPortrait = window.matchMedia('(orientation: portrait)');
      const mqLandscape = window.matchMedia('(orientation: landscape)');
      if (mqPortrait.matches) {
        isPortrait = true;
        isLandscape = false;
      } else if (mqLandscape.matches) {
        isPortrait = false;
        isLandscape = true;
      }
    }

    // Приоритет 4: window.orientation
    if (typeof window.orientation !== 'undefined') {
      const orientation = Math.abs(window.orientation);
      isPortrait = orientation === 0 || orientation === 180;
      isLandscape = orientation === 90 || orientation === -90 || orientation === 270;
    }

    // Приоритет 5: сравнение viewport (fallback)
    if (!isLandscape && !isPortrait) {
      if (viewportWidth > viewportHeight) {
        isLandscape = true;
        isPortrait = false;
      } else {
        isPortrait = true;
        isLandscape = false;
      }
    }

    // Если мы считаем, что ландшафт, но viewport не перевернулся (width < height), меняем местами screenWidth/screenHeight для адаптивной вёрстки
    if (isLandscape && screenWidth < screenHeight) {
      const tmp = screenWidth;
      screenWidth = screenHeight;
      screenHeight = tmp;
    }
    
    // Определение touch-устройства
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Определение размера устройства (брейкпоинты)
    const isSmallScreen = screenWidth < 640; // sm
    const isMediumScreen = screenWidth >= 640 && screenWidth < 1024; // md
    const isLargeScreen = screenWidth >= 1024 && screenWidth < 1280; // lg
    const isXLargeScreen = screenWidth >= 1280; // xl
    
    // Определение окружения (браузер на компе vs браузер на мобилке)
    const isMobileBrowser = isMobile && !isPWA; // Мобильный браузер
    const isDesktopBrowser = isDesktop && !isPWA; // Десктопный браузер
    const isMobilePWA = isMobile && isPWA; // PWA на мобильном
    const isDesktopPWA = isDesktop && isPWA; // PWA на десктопе
    
    // Определение производительности устройства
    const deviceMemory = navigator.deviceMemory || null;
    const hardwareConcurrency = navigator.hardwareConcurrency || null;
    const isLowEndDevice = deviceMemory ? deviceMemory <= 2 : false;
    
    // Определение типа соединения
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const connectionType = connection ? connection.effectiveType : null;
    const isSlowConnection = connectionType === 'slow-2g' || connectionType === '2g';
    
    return {
      // Тип устройства
      isMobile,
      isTablet,
      isDesktop,
      isIOS,
      isAndroid,
      
      // Браузер
      isChrome,
      isFirefox,
      isSafari,
      isEdge,
      userAgent,
      platform,
      
      // PWA
      isPWA,
      isStandalone,
      isInStandaloneMode,
      
      // Размер экрана
      screenWidth,
      screenHeight,
      screenPhysicalWidth,
      screenPhysicalHeight,
      isPortrait,
      isLandscape,
      
      // Touch
      isTouchDevice,
      
      // Брейкпоинты
      isSmallScreen,
      isMediumScreen,
      isLargeScreen,
      isXLargeScreen,
      
      // Окружение
      isMobileBrowser,
      isDesktopBrowser,
      isMobilePWA,
      isDesktopPWA,
      
      // Производительность
      deviceMemory,
      hardwareConcurrency,
      isLowEndDevice,
      
      // Соединение
      connectionType,
      isSlowConnection,
      connection: connection ? {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData
      } : null,

      // Последние показания deviceorientation
      lastOrientation: lastOrientation || { alpha: null, beta: null, gamma: null }
    };
};

export const useDeviceDetection = () => {
  const lastOrientationRef = useRef({ alpha: null, beta: null, gamma: null });
  const [deviceInfo, setDeviceInfo] = useState(() => getDeviceInfoInternal(lastOrientationRef.current));

  // Функция для получения информации об устройстве
  const getDeviceInfo = useCallback(() => {
    const base = getDeviceInfoInternal(lastOrientationRef.current);
    return {
      ...base,
      lastOrientation: lastOrientationRef.current
    };
  }, []);

    // Обновление информации при изменении размера окна
  useEffect(() => {
    const updateDeviceInfo = (event) => {
      // Для orientationchange обновляем немедленно и затем еще раз после задержки
      if (event?.type === 'orientationchange') {
        // Немедленное обновление
        setDeviceInfo(getDeviceInfo());
        // Дополнительные обновления для PWA (размеры могут обновляться с задержкой)
        setTimeout(() => {
          setDeviceInfo(getDeviceInfo());
        }, 100);
        setTimeout(() => {
          setDeviceInfo(getDeviceInfo());
        }, 300);
        setTimeout(() => {
          setDeviceInfo(getDeviceInfo());
        }, 500);
      } else if (event?.type === 'resize') {
        // Для resize небольшая задержка
        setTimeout(() => {
          setDeviceInfo(getDeviceInfo());
        }, 100);
      } else {
        // Для других событий обновляем немедленно
        setDeviceInfo(getDeviceInfo());
      }
    };

    // Обновляем при изменении размера окна
    window.addEventListener('resize', updateDeviceInfo);
    
    // Обновляем при изменении ориентации
    window.addEventListener('orientationchange', updateDeviceInfo);
    
    // Используем screen.orientation API если доступен
    if (window.screen && window.screen.orientation && window.screen.orientation.addEventListener) {
      window.screen.orientation.addEventListener('change', updateDeviceInfo);
    }
    
    // Также обновляем при изменении размеров (для случаев, когда orientationchange не срабатывает)
    const mediaQueryPortrait = window.matchMedia('(orientation: portrait)');
    const mediaQueryLandscape = window.matchMedia('(orientation: landscape)');
    const handleOrientationChange = () => {
      setTimeout(() => {
        setDeviceInfo(getDeviceInfo());
      }, 300);
    };
    mediaQueryPortrait.addEventListener('change', handleOrientationChange);
    mediaQueryLandscape.addEventListener('change', handleOrientationChange);
    
    // Обновляем при изменении display-mode (PWA)
    const pwaMediaQuery = window.matchMedia('(display-mode: standalone)');
    pwaMediaQuery.addEventListener('change', updateDeviceInfo);
    
    // Для PWA: используем visualViewport API если доступен (более точно для определения размеров)
    let visualViewportResizeHandler = null;
    if (window.visualViewport) {
      visualViewportResizeHandler = () => {
        setTimeout(() => {
          setDeviceInfo(getDeviceInfo());
        }, 300);
      };
      window.visualViewport.addEventListener('resize', visualViewportResizeHandler);
      window.visualViewport.addEventListener('scroll', visualViewportResizeHandler);
    }

    // Доп. обработчик deviceorientation для PWA (определяем наклон, если viewport не меняется)
    let orientationThrottleTimeout = null;
    let lastGamma = null;
    let hasDeviceOrientationPermission = false;
    
    const handleDeviceOrientation = (event) => {
      const { alpha = null, beta = null, gamma = null } = event;
      lastOrientationRef.current = { alpha, beta, gamma };
      hasDeviceOrientationPermission = true;
      
      // Если gamma изменился значительно (более чем на 30 градусов), обновляем немедленно
      if (lastGamma !== null && gamma !== null && Math.abs(lastGamma - gamma) > 30) {
        if (orientationThrottleTimeout) {
          clearTimeout(orientationThrottleTimeout);
        }
        lastGamma = gamma;
        // Немедленное обновление при значительном изменении
        setDeviceInfo(getDeviceInfo());
        // Дополнительное обновление через небольшую задержку
        setTimeout(() => {
          setDeviceInfo(getDeviceInfo());
        }, 100);
        return;
      }
      
      lastGamma = gamma;
      
      // Throttle обновления для небольших изменений (максимум раз в 300ms)
      if (orientationThrottleTimeout) {
        clearTimeout(orientationThrottleTimeout);
      }
      orientationThrottleTimeout = setTimeout(() => {
        setDeviceInfo(getDeviceInfo());
      }, 300);
    };

    // Запрос разрешения для deviceorientation (если доступно)
    if (typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function') {
      DeviceOrientationEvent.requestPermission()
        .then(response => {
          if (response === 'granted') {
            window.addEventListener('deviceorientation', handleDeviceOrientation);
          }
        })
        .catch(() => {
          // Если запрос разрешения не поддерживается, просто добавляем слушатель
          window.addEventListener('deviceorientation', handleDeviceOrientation);
        });
    } else {
      // Если запрос разрешения не нужен, просто добавляем слушатель
      window.addEventListener('deviceorientation', handleDeviceOrientation);
    }

    // Периодическая проверка ориентации (каждые 500ms) для надежности
    const orientationCheckInterval = setInterval(() => {
      setDeviceInfo(getDeviceInfo());
    }, 500);

    return () => {
      clearInterval(orientationCheckInterval);
      window.removeEventListener('resize', updateDeviceInfo);
      window.removeEventListener('orientationchange', updateDeviceInfo);
      if (window.screen && window.screen.orientation && window.screen.orientation.removeEventListener) {
        window.screen.orientation.removeEventListener('change', updateDeviceInfo);
      }
      mediaQueryPortrait.removeEventListener('change', handleOrientationChange);
      mediaQueryLandscape.removeEventListener('change', handleOrientationChange);
      pwaMediaQuery.removeEventListener('change', updateDeviceInfo);
      if (window.visualViewport && visualViewportResizeHandler) {
        window.visualViewport.removeEventListener('resize', visualViewportResizeHandler);
        window.visualViewport.removeEventListener('scroll', visualViewportResizeHandler);
      }
      // Удаляем слушатель deviceorientation (даже если он был добавлен условно)
      try {
        window.removeEventListener('deviceorientation', handleDeviceOrientation);
      } catch (e) {
        // Игнорируем ошибки при удалении
      }
      if (orientationThrottleTimeout) {
        clearTimeout(orientationThrottleTimeout);
      }
    };
  }, [getDeviceInfo]);

  // Обновление информации при изменении соединения
  useEffect(() => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return;

    const updateConnection = () => {
      setDeviceInfo(prev => ({
        ...prev,
        connectionType: connection.effectiveType,
        isSlowConnection: connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g',
        connection: {
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt,
          saveData: connection.saveData
        }
      }));
    };

    connection.addEventListener('change', updateConnection);
    return () => connection.removeEventListener('change', updateConnection);
  }, []);

  return deviceInfo;
};

/**
 * Утилита для получения класса устройства для условного рендеринга
 */
export const getDeviceClass = (deviceInfo) => {
  if (!deviceInfo) return '';
  
  const classes = [];
  
  if (deviceInfo.isMobile) classes.push('device-mobile');
  if (deviceInfo.isTablet) classes.push('device-tablet');
  if (deviceInfo.isDesktop) classes.push('device-desktop');
  if (deviceInfo.isPWA) classes.push('device-pwa');
  if (deviceInfo.isTouchDevice) classes.push('device-touch');
  if (deviceInfo.isSmallScreen) classes.push('screen-small');
  if (deviceInfo.isMediumScreen) classes.push('screen-medium');
  if (deviceInfo.isLargeScreen) classes.push('screen-large');
  if (deviceInfo.isXLargeScreen) classes.push('screen-xlarge');
  
  return classes.join(' ');
};

export default useDeviceDetection;

